import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Save, ArrowRight, ArrowLeft, ChevronDown, ChevronRight, Download, FileText, FileSpreadsheet, UserPlus, Building2, MapPin, Send, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../lib/api';
import { PERMISSIONS, hasPermission } from '../lib/permissions';

// Pre-populated bid sheet template with Category -> Subcategory structure
const DEFAULT_BID_TEMPLATE = [
  {
    category: 'Site Work',
    subcategories: [
      { name: 'Survey & Staking', items: ['Boundary Survey', 'Construction Staking'] },
      { name: 'Site Preparation', items: ['Erosion Control', 'Excavation', 'Grading'] },
      { name: 'Utilities', items: ['Water Tap', 'Sewer Tap', 'Gas Connection', 'Electric Connection'] },
    ]
  },
  {
    category: 'Foundation',
    subcategories: [
      { name: 'Footings', items: ['Footing Forms', 'Footings Pour'] },
      { name: 'Walls', items: ['Foundation Walls', 'Wall Cure'] },
      { name: 'Waterproofing', items: ['Waterproofing', 'Drain Tile', 'Backfill'] },
    ]
  },
  {
    category: 'Concrete & Slab',
    subcategories: [
      { name: 'Plumbing', items: ['Ground Plumbing', 'Plumbing Inspection'] },
      { name: 'Slab Work', items: ['Slab Prep (Gravel, Vapor Barrier)', 'Slab Pour', 'Slab Cure'] },
    ]
  },
  {
    category: 'Framing',
    subcategories: [
      { name: 'Structure', items: ['Rough Framing', 'Roof Framing', 'Sheathing'] },
      { name: 'Materials', items: ['Lumber Package', 'Trusses', 'Hardware'] },
    ]
  },
  {
    category: 'Exterior',
    subcategories: [
      { name: 'Windows & Doors', items: ['Windows', 'Entry Doors', 'Garage Door'] },
      { name: 'Roofing', items: ['Roofing Materials', 'Roofing Labor'] },
      { name: 'Siding & Trim', items: ['Siding', 'Soffit & Fascia', 'Gutters'] },
    ]
  },
  {
    category: 'Rough-Ins',
    subcategories: [
      { name: 'Plumbing', items: ['Plumbing Rough-In', 'Plumbing Inspection'] },
      { name: 'HVAC', items: ['HVAC Rough-In', 'HVAC Inspection', 'HVAC Equipment'] },
      { name: 'Electrical', items: ['Electrical Rough-In', 'Electrical Inspection'] },
    ]
  },
  {
    category: 'Insulation & Drywall',
    subcategories: [
      { name: 'Insulation', items: ['Insulation Installation', 'Insulation Inspection'] },
      { name: 'Drywall', items: ['Drywall Materials', 'Drywall Hanging', 'Taping & Finishing'] },
    ]
  },
  {
    category: 'Interior Finishes',
    subcategories: [
      { name: 'Trim & Doors', items: ['Interior Trim', 'Interior Doors'] },
      { name: 'Paint', items: ['Interior Paint', 'Exterior Paint'] },
      { name: 'Flooring', items: ['Hardwood', 'Tile', 'Carpet', 'LVP'] },
      { name: 'Cabinets & Counters', items: ['Kitchen Cabinets', 'Bathroom Vanities', 'Countertops'] },
    ]
  },
  {
    category: 'Final Fixtures',
    subcategories: [
      { name: 'Plumbing Fixtures', items: ['Sinks', 'Toilets', 'Showers/Tubs', 'Faucets'] },
      { name: 'Electrical Fixtures', items: ['Light Fixtures', 'Switches & Outlets', 'Ceiling Fans'] },
      { name: 'Appliances', items: ['Kitchen Appliances', 'Washer/Dryer'] },
    ]
  },
  {
    category: 'Final & Cleanup',
    subcategories: [
      { name: 'Inspections', items: ['Final Inspection', 'Certificate of Occupancy'] },
      { name: 'Cleanup', items: ['Construction Cleanup', 'Final Clean'] },
    ]
  },
];

export default function BidSheet({ user }) {
  const { bidSheetId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bidSheet, setBidSheet] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [bidItems, setBidItems] = useState([]);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);
  const [isAddContractorDialogOpen, setIsAddContractorDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Role-based permissions state
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [teamRoles, setTeamRoles] = useState([]);
  
  // Permission check helper
  const can = (permission) => {
    if (!isTeamMember) return true; // Owner has all permissions
    return hasPermission(teamRoles, permission);
  };
  
  const [projectDates, setProjectDates] = useState({
    start_date: '',
    completion_date: '',
  });
  const [projectFinancials, setProjectFinancials] = useState({
    construction_budget: 0,
    borrower_equity: 0,
    estimated_sale_price: 0,
    interest_rate: 0,
    interest_type: 'fixed',
    contingency_budget: 0,
    purchase_price: 0,
    insurance: 0,
  });
  const [newContractor, setNewContractor] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    specialty: '',
  });
  const [borrowerInfo, setBorrowerInfo] = useState({
    borrower_name: '',
    borrower_entity: '',
    property_address: '',
    short_legal: '',
    city: '',
  });
  
  // Submit to bank state
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bankSubmitInfo, setBankSubmitInfo] = useState({
    bank_email: '',
    loan_officer_name: '',
    custom_message: '',
  });

  useEffect(() => {
    loadData();
  }, [bidSheetId]);

  const loadData = async () => {
    try {
      // Check team membership for permissions
      if (user?.id) {
        try {
          const accessRes = await apiService.getTeamAccess(user.id);
          if (accessRes.data && accessRes.data.length > 0) {
            const access = accessRes.data[0];
            const roles = access.roles && access.roles.length > 0 
              ? access.roles 
              : [access.role || 'accountant'];
            setTeamRoles(roles);
            setIsTeamMember(true);
          } else {
            setIsTeamMember(false);
            setTeamRoles([]);
          }
        } catch (e) {
          setIsTeamMember(false);
          setTeamRoles([]);
        }
      }
      
      // Handle "new" bid sheet case
      if (bidSheetId === 'new') {
        // Get user's contractors
        if (user?.id) {
          try {
            const contractorsRes = await apiService.getContractorsByUser(user.id);
            setContractors(contractorsRes.data || []);
          } catch (err) {
            console.log('No contractors found');
            setContractors([]);
          }
        }
        
        // Check for URL params from bid import
        const fromBidId = searchParams.get('from_bid');
        const importCategory = searchParams.get('category');
        const importSubcategory = searchParams.get('subcategory');
        const importItemType = searchParams.get('item_type') || 'labor';
        const importTitle = searchParams.get('title');
        const importTradeType = searchParams.get('trade_type');
        const importContractorId = searchParams.get('contractor_id');
        const importContractorName = searchParams.get('contractor_name');
        const importAmount = searchParams.get('amount');
        const importMethod = searchParams.get('import_method') || 'combined';
        const lineItemsParam = searchParams.get('line_items');
        
        // Initialize with template for new bid sheet
        const initialItems = [];
        let orderIndex = 0;
        
        // If we have import params, add the imported items first
        if (fromBidId && (importCategory || importTitle)) {
          const categoryName = importCategory || importTradeType || 'Imported Bids';
          const subcategoryName = importSubcategory || importTradeType || 'General';
          
          if (importMethod === 'separate' && lineItemsParam) {
            // Parse and add separate line items
            try {
              const lineItems = JSON.parse(decodeURIComponent(lineItemsParam));
              lineItems.forEach((item, idx) => {
                initialItems.push({
                  id: `imported-${fromBidId}-${idx}`,
                  category: categoryName,
                  subcategory: subcategoryName,
                  phase_name: item.description || `${importTitle} - Item ${idx + 1}`,
                  contractor_id: importContractorId || '',
                  contractor_name: importContractorName || '',
                  item_type: item.type || 'labor',
                  estimated_cost: parseFloat(item.amount) || 0,
                  notes: `Imported from bid request`,
                  order_index: orderIndex++,
                  from_bid_id: fromBidId,
                });
              });
              toast.success(`${lineItems.length} line items imported to ${categoryName} > ${subcategoryName}`);
            } catch (e) {
              console.error('Failed to parse line items:', e);
            }
          } else {
            // Combined import - single line item
            initialItems.push({
              id: `imported-${fromBidId}`,
              category: categoryName,
              subcategory: subcategoryName,
              phase_name: importTitle || 'Imported Bid Item',
              contractor_id: importContractorId || '',
              contractor_name: importContractorName || '',
              item_type: importItemType,
              estimated_cost: parseFloat(importAmount) || 0,
              notes: `Imported from bid request`,
              order_index: orderIndex++,
              from_bid_id: fromBidId,
            });
            toast.success(`Bid "${importTitle}" imported to ${categoryName} > ${subcategoryName}`);
          }
        }
        
        // Add default template items
        DEFAULT_BID_TEMPLATE.forEach(cat => {
          cat.subcategories.forEach(sub => {
            sub.items.forEach(item => {
              initialItems.push({
                id: `temp-${orderIndex}`,
                category: cat.category,
                subcategory: sub.name,
                phase_name: item,
                contractor_id: '',
                contractor_name: '',
                item_type: 'labor',
                estimated_cost: 0,
                notes: '',
                order_index: orderIndex++,
              });
            });
          });
        });
        
        setBidItems(initialItems);
        setBidSheet({ id: 'new', user_id: user?.id, name: importTitle ? `Bid Sheet - ${importTitle}` : 'New Bid Sheet' });
        setIsLoading(false);
        return;
      }

      const [sheetRes, bidItemsRes] = await Promise.all([
        apiService.getBidSheet(bidSheetId),
        apiService.getBidSheetItems(bidSheetId),
      ]);

      setBidSheet(sheetRes.data);
      
      // Set borrower info from bid sheet
      setBorrowerInfo({
        borrower_name: sheetRes.data.borrower_name || '',
        borrower_entity: sheetRes.data.borrower_entity || '',
        property_address: sheetRes.data.property_address || '',
        short_legal: sheetRes.data.short_legal || '',
        city: sheetRes.data.city || '',
      });
      
      // Get user's contractors
      const userId = sheetRes.data.user_id;
      if (userId) {
        try {
          const contractorsRes = await apiService.getContractorsByUser(userId);
          setContractors(contractorsRes.data || []);
        } catch (err) {
          console.log('No contractors found');
          setContractors([]);
        }
      }

      // Priority for loading items:
      // 1. If bid_sheet_items exist (user has saved edits), use those
      // 2. If only ai_mappings exist (fresh AI-mapped sheet), convert those
      // 3. If neither, initialize with template
      
      if (bidItemsRes.data.length > 0) {
        // Use saved bid_sheet_items (user has already saved)
        setBidItems(bidItemsRes.data);
      } else if (sheetRes.data.ai_mappings && sheetRes.data.ai_mappings.length > 0) {
        // Convert AI mappings to bid items format for display
        // Categorize item_type based on bank code:
        // 100-400 = Labor (construction)
        // 500 = Material/Labor mix (finishes)
        // 600-700 = Material (fixtures, appliances)
        // 800 = Soft Costs (professional fees, permits)
        // OC = Other (outside contract)
        const getItemType = (bankCode) => {
          if (!bankCode) return 'labor';
          const code = bankCode.toString().trim();
          if (code.startsWith('OC')) return 'other';
          const numCode = parseInt(code.substring(0, 1));
          if (numCode >= 8) return 'soft_costs';  // 800s = professional fees, permits
          if (numCode >= 6) return 'material';    // 600-700 = fixtures, appliances
          if (numCode === 5) {
            // 500s - check if it contains "material" keyword
            return 'material';  // Flooring, cabinets, etc. are typically materials
          }
          return 'labor';  // 100-400 = construction labor
        };
        
        const aiItems = sheetRes.data.ai_mappings.map((mapping, idx) => ({
          id: `ai-${idx}`,
          category: mapping.bank_category?.split(' ')[0] || 'Mapped',
          subcategory: mapping.bank_code || 'AI Mapped',
          phase_name: mapping.user_item || mapping.bank_category || 'Mapped Item',
          contractor_id: '',
          contractor_name: '',
          item_type: getItemType(mapping.bank_code),
          estimated_cost: mapping.user_amount || 0,
          notes: `Bank: ${mapping.bank_code} - ${mapping.bank_category}`,
          order_index: idx,
          bank_code: mapping.bank_code,
          confidence: mapping.confidence,
        }));
        setBidItems(aiItems);
      } else {
        // Initialize with template
        const initialItems = [];
        let orderIndex = 0;
        DEFAULT_BID_TEMPLATE.forEach(cat => {
          cat.subcategories.forEach(sub => {
            sub.items.forEach(item => {
              initialItems.push({
                id: `temp-${orderIndex}`,
                category: cat.category,
                subcategory: sub.name,
                phase_name: item,
                contractor_id: '',
                contractor_name: '',
                item_type: 'labor',
                estimated_cost: 0,
                notes: '',
                order_index: orderIndex++,
              });
            });
          });
        });
        setBidItems(initialItems);
      }
    } catch (error) {
      toast.error('Failed to load bid sheet');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const updateItem = (index, field, value) => {
    const updated = [...bidItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // If contractor is selected, update contractor_name too
    if (field === 'contractor_id' && value) {
      const contractor = contractors.find(c => c.id === value);
      if (contractor) {
        updated[index].contractor_name = contractor.company || contractor.name;
      }
    }
    
    setBidItems(updated);
  };

  const addCustomRow = (category = 'Custom', subcategory = 'Custom') => {
    const newItem = {
      id: `temp-${Date.now()}`,
      category: category,
      subcategory: subcategory,
      phase_name: '',
      contractor_id: '',
      contractor_name: '',
      item_type: 'labor',
      estimated_cost: 0,
      notes: '',
      order_index: bidItems.length,
    };
    setBidItems([...bidItems, newItem]);
  };

  const deleteRow = (index) => {
    setBidItems(bidItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return bidItems.reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0);
  };

  const calculateLaborTotal = () => {
    return bidItems
      .filter(item => item.item_type === 'labor' || item.item_type === 'labor_and_materials')
      .reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0);
  };

  const calculateMaterialTotal = () => {
    return bidItems
      .filter(item => item.item_type === 'material')
      .reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0);
  };

  const calculateSoftCostsTotal = () => {
    return bidItems
      .filter(item => item.item_type === 'soft_costs')
      .reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0);
  };

  const calculateOtherCostsTotal = () => {
    return bidItems
      .filter(item => item.item_type === 'other')
      .reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // For AI-mapped bid sheets, we need to update the ai_mappings field
      // AND create bid_sheet_items for persistence
      const isAIMapped = bidSheet?.ai_mappings && bidSheet.ai_mappings.length > 0;
      
      // Delete existing bid_sheet_items
      const existingItems = await apiService.getBidSheetItems(bidSheetId);
      for (const item of existingItems.data) {
        await apiService.deleteBidSheetItem(item.id);
      }

      // Create new items in bid_sheet_items collection
      for (let i = 0; i < bidItems.length; i++) {
        const item = bidItems[i];
        await apiService.createBidSheetItem(bidSheetId, {
          category: item.category,
          subcategory: item.subcategory || '',
          phase_name: item.phase_name,
          contractor_id: item.contractor_id || null,
          contractor_name: item.contractor_name || null,
          item_type: item.item_type || 'labor',
          estimated_cost: parseFloat(item.estimated_cost || 0),
          notes: item.notes || null,
          order_index: i,
        });
      }

      // If this was an AI-mapped bid sheet, also update the ai_mappings
      // so both sources stay in sync
      if (isAIMapped) {
        const updatedMappings = bidItems.map((item, idx) => ({
          bank_code: item.subcategory || item.bank_code || '',
          bank_category: item.notes?.replace('Bank: ', '') || '',
          user_item: item.phase_name,
          user_amount: parseFloat(item.estimated_cost || 0),
          confidence: item.confidence || 'high',
        }));
        
        await apiService.updateBidSheet(bidSheetId, {
          ai_mappings: updatedMappings,
        });
      }

      // Update bid sheet with borrower info and total cost
      await apiService.updateBidSheet(bidSheetId, {
        total_cost: calculateTotal(),
        borrower_name: borrowerInfo.borrower_name,
        borrower_entity: borrowerInfo.borrower_entity,
        property_address: borrowerInfo.property_address,
        short_legal: borrowerInfo.short_legal,
        city: borrowerInfo.city,
      });

      toast.success('Bid sheet saved!');
    } catch (error) {
      toast.error('Failed to save bid sheet');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      // Save first
      await handleSave();
      
      const response = await apiService.exportBidSheetCSV(bidSheetId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BidSheet_${bidSheet?.name || 'export'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully!');
    } catch (error) {
      toast.error('Failed to export CSV');
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      // Save first
      await handleSave();
      
      const response = await apiService.exportBidSheetPDF(bidSheetId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BidSheet_${bidSheet?.name || 'export'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleStartProject = async () => {
    // Save first
    await handleSave();
    
    // Pre-populate construction budget with bid total
    setProjectFinancials(prev => ({
      ...prev,
      construction_budget: calculateTotal(),
    }));
    
    // Open date picker dialog
    setIsDateDialogOpen(true);
  };

  const handleConfirmStartProject = async () => {
    if (!projectDates.start_date || !projectDates.completion_date) {
      toast.error('Please select start and completion dates');
      return;
    }

    setIsConverting(true);
    try {
      // Update bid sheet with dates and financials before converting
      await apiService.updateBidSheet(bidSheetId, {
        start_date: projectDates.start_date,
        completion_date: projectDates.completion_date,
      });

      // Convert bid sheet to project with phases and include financials
      const projectRes = await apiService.convertBidSheetToProject(bidSheetId);
      const newProjectId = projectRes.data.project_id;
      
      // Update the created project with financial data
      await apiService.updateProject(newProjectId, {
        construction_budget: projectFinancials.construction_budget,
        borrower_equity: projectFinancials.borrower_equity,
        estimated_sale_price: projectFinancials.estimated_sale_price,
        loan_interest_rate: projectFinancials.interest_rate,
        loan_interest_type: projectFinancials.interest_type,
        contingency_budget: projectFinancials.contingency_budget,
        purchase_price_land: projectFinancials.purchase_price,
        insurance: projectFinancials.insurance,
      });

      toast.success('Project started! Phases created with AI scheduling.');
      navigate(`/project/${newProjectId}`);
    } catch (error) {
      toast.error('Failed to start project');
      console.error(error);
    } finally {
      setIsConverting(false);
      setIsDateDialogOpen(false);
    }
  };

  const handleAddContractor = async () => {
    if (!newContractor.name) {
      toast.error('Contractor name is required');
      return;
    }

    try {
      const response = await apiService.createContractor({
        user_id: bidSheet.user_id,
        project_id: null, // Global contractor
        name: newContractor.name,
        company: newContractor.company,
        email: newContractor.email,
        phone: newContractor.phone,
        specialty: newContractor.specialty,
      });
      
      setContractors([...contractors, response.data]);
      setNewContractor({ name: '', company: '', email: '', phone: '', specialty: '' });
      setIsAddContractorDialogOpen(false);
      toast.success('Contractor added successfully!');
    } catch (error) {
      toast.error('Failed to add contractor');
      console.error(error);
    }
  };

  const handleSubmitToBank = async () => {
    setIsSubmitting(true);
    try {
      // Save the bid sheet first to ensure borrower info is current
      await handleSave();
      
      const response = await apiService.submitBidSheetToBank(
        bidSheetId,
        user.id,
        bankSubmitInfo.bank_email || null,
        bankSubmitInfo.loan_officer_name || null,
        bankSubmitInfo.custom_message || null
      );
      
      if (response.data.email_sent) {
        toast.success(`Bid sheet submitted and emailed to ${bankSubmitInfo.bank_email}!`);
      } else if (bankSubmitInfo.bank_email) {
        toast.success('Bid sheet submitted! Email could not be sent - please send manually.');
      } else {
        toast.success('Bid sheet marked as submitted to bank!');
      }
      
      setIsSubmitDialogOpen(false);
      setBankSubmitInfo({ bank_email: '', loan_officer_name: '', custom_message: '' });
      
      // Reload the bid sheet to get updated status
      loadData();
    } catch (error) {
      toast.error('Failed to submit bid sheet');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportBankFormat = async () => {
    if (!bidSheet?.bank_template_id) {
      toast.error('This bid sheet does not have an associated bank template');
      return;
    }
    
    setIsExporting(true);
    try {
      const response = await apiService.exportBidSheetToBankFormat(bidSheetId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${bidSheet.name.replace(/\s+/g, '_')}_BankFormat.xlsm`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to bank format!');
    } catch (error) {
      toast.error('Failed to export bank format');
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Group items by category and subcategory
  const itemsByCategory = bidItems.reduce((acc, item, index) => {
    const cat = item.category || 'Uncategorized';
    const sub = item.subcategory || 'General';
    
    if (!acc[cat]) {
      acc[cat] = { subcategories: {} };
    }
    if (!acc[cat].subcategories[sub]) {
      acc[cat].subcategories[sub] = [];
    }
    acc[cat].subcategories[sub].push({ ...item, originalIndex: index });
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white border-none shadow-xl">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
              <div>
                <CardTitle className="text-2xl sm:text-3xl">📋 Bid Sheet: {bidSheet?.name}</CardTitle>
                <CardDescription className="text-blue-100 mt-1">
                  Category → Subcategory → Contractor → Type → Cost
                </CardDescription>
              </div>
              <Button
                onClick={() => navigate('/bid-sheets')}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100">Labor</p>
                <p className="text-lg font-bold">${calculateLaborTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100">Material</p>
                <p className="text-lg font-bold">${calculateMaterialTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100">Soft Costs</p>
                <p className="text-lg font-bold">${calculateSoftCostsTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs text-blue-100">Other</p>
                <p className="text-lg font-bold">${calculateOtherCostsTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white/20 rounded-lg p-3 col-span-2 sm:col-span-1">
                <p className="text-xs text-blue-100">Grand Total</p>
                <p className="text-xl font-bold">${calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                variant="outline"
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={handleExportCSV}
                disabled={isExporting}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button
                onClick={handleExportPDF}
                disabled={isExporting}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
              {bidSheet?.bank_template_id && (
                <Button
                  onClick={handleExportBankFormat}
                  disabled={isExporting}
                  variant="outline"
                  className="bg-amber-500 text-white border-amber-400 hover:bg-amber-600"
                  data-testid="export-bank-format-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Bank Format
                </Button>
              )}
              <Button
                onClick={() => setIsAddContractorDialogOpen(true)}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Contractor
              </Button>
              <Button
                onClick={() => setIsSubmitDialogOpen(true)}
                variant="outline"
                className="bg-purple-500 text-white border-purple-400 hover:bg-purple-600"
                data-testid="submit-to-bank-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit to Bank
              </Button>
              <Button
                onClick={handleStartProject}
                disabled={isConverting || calculateTotal() === 0}
                className="bg-green-600 hover:bg-green-700 text-white ml-auto"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                {isConverting ? 'Converting...' : 'Start Project'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Borrower Information Section */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Borrower Information
            </CardTitle>
            <CardDescription>Required for bank loan approval</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="borrower_name">Borrower Name</Label>
                <Input
                  id="borrower_name"
                  value={borrowerInfo.borrower_name}
                  onChange={(e) => setBorrowerInfo({ ...borrowerInfo, borrower_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="borrower_entity">Borrower Entity (LLC/Corp)</Label>
                <Input
                  id="borrower_entity"
                  value={borrowerInfo.borrower_entity}
                  onChange={(e) => setBorrowerInfo({ ...borrowerInfo, borrower_entity: e.target.value })}
                  placeholder="Smith Development LLC"
                />
                <p className="text-xs text-slate-500">The development entity used for this project</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={borrowerInfo.city}
                  onChange={(e) => setBorrowerInfo({ ...borrowerInfo, city: e.target.value })}
                  placeholder="Austin"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="property_address">Street Address</Label>
                <Input
                  id="property_address"
                  value={borrowerInfo.property_address}
                  onChange={(e) => setBorrowerInfo({ ...borrowerInfo, property_address: e.target.value })}
                  placeholder="123 Oak Street"
                />
              </div>
              <div className="space-y-2 lg:col-span-3">
                <Label htmlFor="short_legal">Short Legal Description</Label>
                <Textarea
                  id="short_legal"
                  value={borrowerInfo.short_legal}
                  onChange={(e) => setBorrowerInfo({ ...borrowerInfo, short_legal: e.target.value })}
                  placeholder="Lot 5, Block 3, Oak Hills Subdivision, Phase 2"
                  rows={2}
                />
                <p className="text-xs text-slate-500">Legal description of the lot from your title documents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bid Sheet Table */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-4">
              {Object.entries(itemsByCategory).map(([category, catData]) => (
                <div key={category} className="border rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <div
                    className="flex items-center gap-2 p-3 bg-blue-600 text-white cursor-pointer hover:bg-blue-700 transition-colors"
                    onClick={() => toggleCategory(category)}
                  >
                    {collapsedCategories[category] ? (
                      <ChevronRight className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                    <h3 className="font-bold text-lg">{category}</h3>
                    <span className="text-sm text-blue-200 ml-2">
                      ({Object.values(catData.subcategories).flat().length} items)
                    </span>
                  </div>

                  {/* Subcategories */}
                  {!collapsedCategories[category] && (
                    <div className="bg-white">
                      {Object.entries(catData.subcategories).map(([subcategory, items]) => (
                        <div key={subcategory} className="border-b last:border-b-0">
                          {/* Subcategory Header */}
                          <div className="bg-slate-100 px-4 py-2 font-medium text-slate-700">
                            {subcategory}
                          </div>
                          
                          {/* Items Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-slate-50 text-xs text-slate-500">
                                <tr>
                                  <th className="px-3 py-2 text-left w-[25%]">Description</th>
                                  <th className="px-3 py-2 text-left w-[22%]">Contractor</th>
                                  <th className="px-3 py-2 text-left w-[12%]">Type</th>
                                  <th className="px-3 py-2 text-left w-[15%]">Cost</th>
                                  <th className="px-3 py-2 text-left w-[20%]">Notes</th>
                                  <th className="px-3 py-2 text-center w-[6%]"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((item) => (
                                  <tr key={item.originalIndex} className="border-t hover:bg-slate-50">
                                    <td className="px-3 py-2">
                                      <Input
                                        value={item.phase_name}
                                        onChange={(e) => updateItem(item.originalIndex, 'phase_name', e.target.value)}
                                        placeholder="Description"
                                        className="h-9"
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <Select
                                        value={item.contractor_id || 'none'}
                                        onValueChange={(value) => {
                                          if (value === 'add_new') {
                                            setIsAddContractorDialogOpen(true);
                                          } else {
                                            updateItem(item.originalIndex, 'contractor_id', value === 'none' ? '' : value);
                                          }
                                        }}
                                      >
                                        <SelectTrigger className="h-9">
                                          <SelectValue placeholder="Select..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="none">No contractor</SelectItem>
                                          {contractors.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                              {c.company || c.name}
                                            </SelectItem>
                                          ))}
                                          <div className="border-t my-1" />
                                          <SelectItem value="add_new">
                                            <div className="flex items-center gap-2 text-blue-600">
                                              <UserPlus className="w-4 h-4" />
                                              Add New Contractor
                                            </div>
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="px-3 py-2">
                                      <Select
                                        value={item.item_type || 'labor'}
                                        onValueChange={(value) => updateItem(item.originalIndex, 'item_type', value)}
                                      >
                                        <SelectTrigger className="h-9">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="labor">Labor</SelectItem>
                                          <SelectItem value="material">Material</SelectItem>
                                          <SelectItem value="labor_and_materials">Labor & Materials</SelectItem>
                                          <SelectItem value="soft_costs">Soft Costs</SelectItem>
                                          <SelectItem value="other">Other Costs</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={item.estimated_cost}
                                          onChange={(e) => updateItem(item.originalIndex, 'estimated_cost', e.target.value)}
                                          placeholder="0.00"
                                          className="h-9 pl-7"
                                        />
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      <Input
                                        value={item.notes || ''}
                                        onChange={(e) => updateItem(item.originalIndex, 'notes', e.target.value)}
                                        placeholder="Notes"
                                        className="h-9"
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deleteRow(item.originalIndex)}
                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Add item to subcategory */}
                          <div className="px-4 py-2 border-t bg-slate-50">
                            <Button
                              onClick={() => addCustomRow(category, subcategory)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Item
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Add Custom Category/Row Button */}
              <Button
                onClick={() => addCustomRow()}
                variant="outline"
                className="w-full border-dashed"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Custom Item
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Start Project Dialog with Financial Confirmation */}
      <Dialog open={isDateDialogOpen} onOpenChange={setIsDateDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">📋 Confirm Project Details</DialogTitle>
            <DialogDescription>
              Review and confirm your financial numbers before starting the project. These may have changed from your initial estimates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Timeline Section */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-700 mb-3">Project Timeline</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Expected Start Date *</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={projectDates.start_date}
                    onChange={(e) => setProjectDates({ ...projectDates, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="completion-date">Expected Completion Date *</Label>
                  <Input
                    id="completion-date"
                    type="date"
                    value={projectDates.completion_date}
                    onChange={(e) => setProjectDates({ ...projectDates, completion_date: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Financial Confirmation Section */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-700 mb-3">💰 Financial Confirmation</h3>
              <p className="text-sm text-blue-600 mb-4">Re-confirm your numbers for accuracy across the entire project</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="construction_budget">Construction Budget *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="construction_budget"
                      type="number"
                      min="0"
                      step="0.01"
                      value={projectFinancials.construction_budget}
                      onChange={(e) => setProjectFinancials({ ...projectFinancials, construction_budget: e.target.value === '' ? '' : parseFloat(e.target.value) || '' })}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProjectFinancials(prev => ({ ...prev, construction_budget: 0 })); }}
                      className="pl-7"
                      placeholder="From bid sheet total"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Pre-filled from bid total: ${calculateTotal().toLocaleString()}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="borrower_equity">Borrower Cash/Equity</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="borrower_equity"
                      type="number"
                      min="0"
                      step="0.01"
                      value={projectFinancials.borrower_equity}
                      onChange={(e) => setProjectFinancials({ ...projectFinancials, borrower_equity: e.target.value === '' ? '' : parseFloat(e.target.value) || '' })}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProjectFinancials(prev => ({ ...prev, borrower_equity: 0 })); }}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purchase_price">Purchase Price (Lot/Property)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="purchase_price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={projectFinancials.purchase_price}
                      onChange={(e) => setProjectFinancials({ ...projectFinancials, purchase_price: e.target.value === '' ? '' : parseFloat(e.target.value) || '' })}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProjectFinancials(prev => ({ ...prev, purchase_price: 0 })); }}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimated_sale_price">Estimated Sale Price</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="estimated_sale_price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={projectFinancials.estimated_sale_price}
                      onChange={(e) => setProjectFinancials({ ...projectFinancials, estimated_sale_price: e.target.value === '' ? '' : parseFloat(e.target.value) || '' })}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProjectFinancials(prev => ({ ...prev, estimated_sale_price: 0 })); }}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contingency_budget">Contingency Budget</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="contingency_budget"
                      type="number"
                      min="0"
                      step="0.01"
                      value={projectFinancials.contingency_budget}
                      onChange={(e) => setProjectFinancials({ ...projectFinancials, contingency_budget: e.target.value === '' ? '' : parseFloat(e.target.value) || '' })}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProjectFinancials(prev => ({ ...prev, contingency_budget: 0 })); }}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Typical: 5-10% of construction budget</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="insurance">Insurance</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="insurance"
                      type="number"
                      min="0"
                      step="0.01"
                      value={projectFinancials.insurance}
                      onChange={(e) => setProjectFinancials({ ...projectFinancials, insurance: e.target.value === '' ? '' : parseFloat(e.target.value) || '' })}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProjectFinancials(prev => ({ ...prev, insurance: 0 })); }}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                  <Input
                    id="interest_rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.125"
                    value={projectFinancials.interest_rate}
                    onChange={(e) => setProjectFinancials({ ...projectFinancials, interest_rate: e.target.value === '' ? '' : parseFloat(e.target.value) || '' })}
                    onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProjectFinancials(prev => ({ ...prev, interest_rate: 0 })); }}
                    placeholder="e.g., 7.5"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interest_type">Interest Type</Label>
                  <Select
                    value={projectFinancials.interest_type}
                    onValueChange={(value) => setProjectFinancials({ ...projectFinancials, interest_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Rate</SelectItem>
                      <SelectItem value="variable">Variable Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsDateDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmStartProject}
                disabled={isConverting || !projectDates.start_date || !projectDates.completion_date}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {isConverting ? 'Creating Project...' : 'Confirm & Start Project'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contractor Dialog */}
      <Dialog open={isAddContractorDialogOpen} onOpenChange={setIsAddContractorDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Contractor</DialogTitle>
            <DialogDescription>
              Create a new contractor to assign to bid items
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contractor-name">Name *</Label>
                <Input
                  id="contractor-name"
                  value={newContractor.name}
                  onChange={(e) => setNewContractor({ ...newContractor, name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contractor-company">Company</Label>
                <Input
                  id="contractor-company"
                  value={newContractor.company}
                  onChange={(e) => setNewContractor({ ...newContractor, company: e.target.value })}
                  placeholder="Smith Plumbing LLC"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contractor-email">Email</Label>
                <Input
                  id="contractor-email"
                  type="email"
                  value={newContractor.email}
                  onChange={(e) => setNewContractor({ ...newContractor, email: e.target.value })}
                  placeholder="john@smithplumbing.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contractor-phone">Phone</Label>
                <Input
                  id="contractor-phone"
                  value={newContractor.phone}
                  onChange={(e) => setNewContractor({ ...newContractor, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contractor-specialty">Specialty</Label>
              <Input
                id="contractor-specialty"
                value={newContractor.specialty}
                onChange={(e) => setNewContractor({ ...newContractor, specialty: e.target.value })}
                placeholder="Plumbing, HVAC, Electrical, etc."
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setIsAddContractorDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddContractor}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Add Contractor
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit to Bank Dialog */}
      <Dialog open={isSubmitDialogOpen} onOpenChange={setIsSubmitDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-purple-600" />
              Submit to Bank
            </DialogTitle>
            <DialogDescription>
              Submit this bid sheet for bank approval. Optionally send an email with the PDF attached.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">
                <strong>Project:</strong> {bidSheet?.name}
              </p>
              <p className="text-sm text-slate-600 mb-2">
                <strong>Total Budget:</strong> ${calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Address:</strong> {borrowerInfo.property_address || 'Not specified'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bank-email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Bank Email (optional)
              </Label>
              <Input
                id="bank-email"
                type="email"
                value={bankSubmitInfo.bank_email}
                onChange={(e) => setBankSubmitInfo({ ...bankSubmitInfo, bank_email: e.target.value })}
                placeholder="loan.officer@hillsbank.com"
              />
              <p className="text-xs text-slate-500">
                If provided, we'll email the bid sheet PDF to this address
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="loan-officer">Loan Officer Name</Label>
              <Input
                id="loan-officer"
                value={bankSubmitInfo.loan_officer_name}
                onChange={(e) => setBankSubmitInfo({ ...bankSubmitInfo, loan_officer_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-message">Custom Message (optional)</Label>
              <Textarea
                id="custom-message"
                value={bankSubmitInfo.custom_message}
                onChange={(e) => setBankSubmitInfo({ ...bankSubmitInfo, custom_message: e.target.value })}
                placeholder="Please find attached the construction bid sheet for our upcoming project..."
                rows={3}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsSubmitDialogOpen(false)}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitToBank}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {bankSubmitInfo.bank_email ? 'Submit & Email' : 'Mark as Submitted'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
